import getProductsMockFns from "~/mocks/products";
import refundCalculateMockFn from "~/mocks/refundCalculate";
import exchangePaymentMock from "~/mocks/exchangePayment.json";
import { getOrderStatusMock } from "~/mocks/orderStatus.js";
import similarProductsMock from "~/mocks/similarProductsMock.js";
import confirmReturnMock from "~/mocks/confirmReturn.json";
import packingInstructionsMock from "~/mocks/packingInstructions.json";
import { getProductsCombinedPurchasePrice } from "~/cores/appCore";
import { enrichShipmentSolutionsWithCarrierData } from "~/mocks/carriersData.js";
import _ from "lodash";

function jsonStreamWrapper(data) {
    return () => {
        return new Promise(function (resolve) {
            resolve(JSON.stringify(data));
        });
    };
}

// we use these values to mock upload/download complaint images correctly
let complaintImageData = {};
let complaintImageIdCounter = 1;
/**
 * devFetch is used for the mocked version of the app where no (almost) services are being made to Rasputin
 */
export const devFetch = function ({ productsBundleMock, system, mockedServices, mockTimer, brandName, brandData, forceRealReturnReasons }, url, params) {
    let body;
    try {
        // Just a safety because we don't always send JSON formatted data
        body = JSON.parse(params?.body);
    } catch (err) {
        /* empty */
    }

    if (url.includes("/brandnames") || !mockedServices) {
        // Make a regular real fetch request if the mockedServices checkbox is off
        console.log("Real Request", url, { ...params, body });
        return fetch(url, params);
    }
    // We are using this for cypress tests. Did not get cypress spy() to work. Not the best solution but it's also nice sometimes to actually see these logs.
    console.log("Mock Request", url, { ...params, body });

    return new Promise(function (resolve, reject) {
        setTimeout(() => {
            let mockData = null;

            if (url.includes("/login")) {
                // Special treatment for demo / dev. Makes sure we have a new random image seed after each login
                complaintImageData = {};

                let productsMockFns = getProductsMockFns({
                    productsBundleMock,
                });
                let products = productsMockFns.getProducts();
                products.totalAmount = getProductsCombinedPurchasePrice({ loginServiceData: { data: products } });
                if (_.isNil(products?.remainingReturnDays)) {
                    products.remainingReturnDays = 28;
                }
                if (_.isNil(products?.orderCreatedAt)) {
                    products.orderCreatedAt = "2024-06-12T14:29:13.218779489Z";
                }
                if (brandData?.returnReasonConfigs?.length > 0) {
                    products?.products?.forEach((p) => {
                        if (forceRealReturnReasons || !p.returnReasonConfig) {
                            p.returnReasonConfig = brandData?.returnReasonConfigs?.find((c) => c.default === true);
                        }
                    });
                }
                if (body?.withdrawal === true) {
                    // Dev-only: the real backend derives these from shop settings. Override locally via
                    // ?withdrawal_item_mode=... & ?withdrawal_shipment_mode=... ; defaults mirror today's behavior.
                    const params = typeof window !== "undefined" ? new URLSearchParams(window.location.search) : new URLSearchParams();
                    const itemMode = params.get("withdrawal_item_mode") || "WHOLE_ORDER_LOCKED";
                    const shipmentMode = params.get("withdrawal_shipment_mode") || "ACKNOWLEDGMENT_ONLY";
                    const allowExchanges = params.get("withdrawal_allow_exchanges") === "true";
                    const allowComplaints = params.get("withdrawal_allow_complaints") === "true";
                    products.exchangesDisabled = !allowExchanges;
                    products.exchangesComplaintDisabled = !allowExchanges;
                    products.complaintsDisabled = !allowComplaints;
                    products.remainingWithdrawalDays = 14;
                    products.withdrawalItemSelectionMode = itemMode;
                    products.withdrawalShipmentMode = shipmentMode;
                    if (shipmentMode === "ACKNOWLEDGMENT_ONLY") {
                        // No shipment booked: force the single REGISTER_WITHDRAWAL solution so the shipment page is
                        // skipped, and restrict the refund to BASIC.
                        products.shipmentSolutions = [{ type: "REGISTER_WITHDRAWAL", cost: 0 }];
                        products.turnrShippingCost = 0;
                        products.refundOptions = [{ type: "BASIC" }];
                    }
                    // OFFER_SHIPPING keeps the mock's existing refund options, so they follow the price setup.
                    // OFFER_SHIPPING keeps the mock's existing shipment solutions, so the shipment page is shown.
                }
                // The real backend embeds carrierData (icon, display name) on each shipment solution.
                // Mirror that here so shipment option logos render in mocked/demo mode.
                enrichShipmentSolutionsWithCarrierData(products.shipmentSolutions);
                mockData = products;
            } else if (url.includes("product") && url.includes("exchange")) {
                const [, , productId] = url.match("/api/v1/digitalreturn/session/(.*)/product/(.*)/exchange");
                const productsMockFns = getProductsMockFns({
                    productsBundleMock,
                });
                mockData = productsMockFns.getExchangeToProducts?.({
                    productId,
                });
                if (!mockData) {
                    console.error("Found no exchange mock. Please check mock bundles for this customer");
                    mockData = { products: [] };
                }
            } else if (url.includes("/exchange/payment")) {
                mockData = exchangePaymentMock;

                /** Complaint image mocks **/
            } else if (url.includes("complaint/images/prepare")) {
                const [, sessionId, productId] = url.match("/session/(.*)/products/(.*)/complaint/images/prepare");
                mockData = {
                    image: {
                        downloadURL: `complaint-image-download-url-mock/session/${sessionId}/products/${productId}`,
                        id: complaintImageIdCounter,
                        uploadURL: `complaint-image-upload-url-mock/session/${sessionId}/products/${productId}`,
                    },
                };
            } else if (url.includes("complaint-image-upload-url-mock")) {
                const file = params?.body;
                const imageUrl = URL.createObjectURL(file);
                const [, , productId] = url.match("/session/(.*)/products/(.*)");

                if (!complaintImageData[productId]) {
                    complaintImageData[productId] = [];
                }
                complaintImageData[productId].push({
                    downloadURL: imageUrl,
                    id: complaintImageIdCounter,
                });
                complaintImageIdCounter += 1;
                mockData = {};
            } else if (url.includes("complaint-image-download-url-mock")) {
                mockData = {};
            } else if (url.includes("complaint/images")) {
                const [, , productId] = url.match("/session/(.*)/products/(.*)/complaint/images");
                mockData = complaintImageData?.[productId] || [];
                /** Complaint image mocks ends **/
            } else if (url.includes("/status")) {
                mockData = getOrderStatusMock({ brandName });
            } else if (url.includes("/similar")) {
                const [, , productId] = url.match("/api/v1/digitalreturn/session/(.*)/products/(.*)/similar");
                let productsMockFns = getProductsMockFns({
                    productsBundleMock,
                });
                let similarProducts = productsMockFns?.getSimilarProducts?.({
                    productId,
                });
                mockData = similarProducts || similarProductsMock;
            } else if (url.includes("/refund/calculate")) {
                mockData = refundCalculateMockFn(params?.mockData);
            } else if (url.includes("packing-instructions")) {
                mockData = packingInstructionsMock;
            } else if (url.endsWith("/return")) {
                mockData = confirmReturnMock;
            }

            if (mockData) {
                console.log("Mock Response", url, mockData);
                resolve({
                    url,
                    text: jsonStreamWrapper(mockData),
                    ok: true,
                });
            } else {
                // https://api.turnr.se/api/v1/digitalreturn/session/a45e3e67-799f-4e48-be63-96a711632dc8/products/10/exchange_alternatives?
                console.warn("Found no mocked service for " + url);
                reject(new Error("Found no mocked service for " + url));
            }
        }, mockTimer);
    });
};
